/**
 * Stamp a doorstep proof photo with a burned-in evidence strip.
 *
 * Why?
 *   A raw photo is easy to dispute ("that could be any door, any day"). By
 *   drawing the timestamp, PIN code and order id directly INTO the pixels
 *   before upload, the resulting JPEG carries its own metadata — you can
 *   screenshot it, forward it, print it, and the evidence stays.
 *
 * The strip renders bottom-aligned, 12% of the shorter edge tall, black
 * @ 62% opacity so the doorstep is still visible behind it.
 */

const DEFAULT_BRAND = "DialFlow · Delivered proof";

/**
 * @param {string} dataUrl        JPEG/PNG data URL to stamp.
 * @param {object} meta
 * @param {string} [meta.timestamp] ISO string, defaults to now.
 * @param {string} [meta.pin]       6-digit PIN (delivery pin_code).
 * @param {string} [meta.orderId]   Full or short order id.
 * @param {string} [meta.brand]     Top-line brand text.
 * @returns {Promise<string>}       New JPEG data URL with the stamp.
 */
export async function stampProof(dataUrl, meta = {}) {
  if (!dataUrl || !dataUrl.startsWith("data:image/")) return dataUrl;

  const {
    timestamp = new Date().toISOString(),
    pin = "",
    orderId = "",
    brand = DEFAULT_BRAND,
  } = meta;

  const img = await loadImage(dataUrl);
  const w = img.naturalWidth;
  const h = img.naturalHeight;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  // Strip height scales with the shorter edge so it looks right on both
  // portrait and landscape captures.
  const short = Math.min(w, h);
  const stripH = Math.max(64, Math.round(short * 0.12));
  const pad = Math.round(stripH * 0.22);
  const y0 = h - stripH;

  // Semi-transparent black band + subtle top border for legibility.
  ctx.fillStyle = "rgba(0, 0, 0, 0.62)";
  ctx.fillRect(0, y0, w, stripH);
  ctx.fillStyle = "rgba(255, 255, 255, 0.15)";
  ctx.fillRect(0, y0, w, Math.max(1, Math.round(stripH * 0.02)));

  // Two-line stamp:
  //   1) big timestamp (IST)
  //   2) small meta row — brand · PIN · order id
  const timeStr = formatIST(timestamp);
  const orderShort = orderId ? `#${orderId.slice(0, 8).toUpperCase()}` : "";
  const metaLine = [brand, pin && `PIN ${pin}`, orderShort]
    .filter(Boolean)
    .join("  ·  ");

  const timeFont = Math.round(stripH * 0.42);
  const metaFont = Math.round(stripH * 0.22);
  const fontFamily = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "rgba(255, 255, 255, 0.98)";
  ctx.font = `700 ${timeFont}px ${fontFamily}`;
  ctx.fillText(timeStr, pad, y0 + pad + timeFont);

  ctx.fillStyle = "rgba(255, 255, 255, 0.78)";
  ctx.font = `500 ${metaFont}px ${fontFamily}`;
  ctx.fillText(metaLine, pad, y0 + stripH - pad);

  // Accent corner mark on the right so the stamp reads intentional, not
  // like a random watermark. Small orange square to match DialFlow accent.
  const dot = Math.round(stripH * 0.18);
  ctx.fillStyle = "#FF5000";
  ctx.fillRect(w - pad - dot, y0 + pad, dot, dot);

  return canvas.toDataURL("image/jpeg", 0.82);
}

/** IST-formatted "20 Feb 2026, 14:32:07 IST" — human-readable and unambiguous. */
function formatIST(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "Asia/Kolkata",
    }) + " IST";
  } catch {
    return iso;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = src;
  });
}
